import { FC, useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import Modal from "../Modal";
import MenuTab from "../MenuTab";
import { FormikProps } from "formik";
import { LinkText, PrimaryButton, SecondaryButton } from "../Buttons";
import { alertService, getDailyClosures, loaderService } from "../../services";
import { useAppSelector } from "../../store/hooks";
import { PaymentFormValues } from "./PaymentChange";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FormSelect, FormTextInput } from "../FormFields";
import { DailyStoreCloseDTO } from "../../interfaces/Dtos/DailyStoreCloseDTO";
import { BankEnum } from "../../interfaces/BankInterface";
import LoadingIcon from "../LodingIcon";
import {
  AccountInterface,
  Banco,
  PaymentInterface,
  PaymentMethodEnum,
  PaymentStatusEnum,
} from "../../interfaces";
import {
  cleanID,
  currencyExchange,
  currencyExchangeText,
  formatGooglePhone,
  formatGooglePhoneUSA,
  formatName,
  useCurrencyExchanges,
} from "../../utils";

interface ChangeModalProps {
  remaining: number;
  formik: FormikProps<PaymentFormValues>;
  owner: AccountInterface;
  bankList?: Banco[];
  payments: PaymentInterface[];
  openModal: boolean;
  availableChange: number;
  isNatural: boolean;
  isRefund?: boolean;
  hasRetention?: boolean;
  initialTab?: string;
  setOpenModal: (open: boolean) => void;
}

interface CashChangeProps {
  currencyID: number;
  formik: FormikProps<PaymentFormValues>;
  max: number;
}
interface SuperadminOption {
  name: string;
  id: number;
}

// Helper functions
async function handleAmountChange(
  e: React.ChangeEvent<HTMLInputElement>,
  formik: FormikProps<PaymentFormValues>,
  max: number,
) {
  let amount = e.target.valueAsNumber;

  if (e.target.valueAsNumber > max) {
    amount = Math.max(max, 0);
  }
  formik.setFieldValue("amount", +amount.toFixed(2));
}

// Cash Change Component
const CashChange: FC<CashChangeProps> = ({ currencyID, formik, max }) => {
  const [loader, setLoader] = useState(false);

  // Set default values
  useEffect(() => {
    if (currencyID === 1) {
      formik.setFieldValue("currency", "BS");
    } else {
      formik.setFieldValue("currency", "USD");
    }

    if (max === 0) {
      setLoader(true);
    }
    setLoader(false);
    max < 0
      ? formik.setFieldValue("amount", 0)
      : formik.setFieldValue("amount", +max.toFixed(2));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyID, max]);

  return (
    <div className="mb-4">
      <FormTextInput
        name="amount"
        type="number"
        step="any"
        label={`Monto a pagar en ${
          currencyID === 1 ? "Bolívares" : "Dólares"
        } en efectivo`}
        onChange={async (e) => await handleAmountChange(e, formik, max)}
        value={formik.values.amount}
        error={
          formik.touched.amount && formik.errors.amount
            ? formik.errors.amount
            : ""
        }
      />
      {loader && (
        <div className="flex items-center justify-center w-full p-8">
          {loader && <LoadingIcon size="2rem" />}
        </div>
      )}
    </div>
  );
};

const ChangeModal: FC<ChangeModalProps> = ({
  remaining,
  formik,
  owner,
  bankList,
  payments,
  openModal,
  availableChange,
  isNatural,
  isRefund = false,
  hasRetention = false,
  initialTab = "Crédito Inmediato",
  setOpenModal,
}) => {
  const paymentMethods = useAppSelector(
    (state) => state.user.paymentMethods ?? [],
  );
  const firstPaymentDate = payments[0].creationDate ?? new Date();
  const exchanges = useCurrencyExchanges(firstPaymentDate.toString());
  const user = useAppSelector((state) => state.user);
  const [radio, setRadio] = useState(0);
  const [maxBs, setMaxBs] = useState(0);
  const [maxUsd, setMaxUsd] = useState(0);
  const [sumBs, setSumBs] = useState(0);
  const [superadminOption, setSuperadminOption] = useState<SuperadminOption>({
    name: "Procesar pago",
    id: 1,
  });
  const [sumUsd, setSumUsd] = useState(0);
  const [idType, setIdType] = useState("V");
  const [dailyClosures, setDailyClosures] = useState<DailyStoreCloseDTO[]>([]);

  const superadminOptions: SuperadminOption[] = [
    {
      name: "Procesar vuelto",
      id: 1,
    },
    {
      name: "Registrar vuelto",
      id: 2,
    },
  ];

  // Identification types
  const identificationTypes = useMemo(() => {
    let types = ["V", "E"];
    if (
      !!formik.values.paymentMethod &&
      formik.values.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.CREDITO_INMEDIATO
    ) {
      types.push("J", "G");
    }
    return types;
  }, [formik.values.paymentMethod]);

  const reintegroMethod = useMemo(
    () =>
      paymentMethods.find(
        (method) => method.paymentMethodID === PaymentMethodEnum.REINTEGRO,
      ),
    [paymentMethods],
  );
  const b2pMethod = useMemo(
    () =>
      paymentMethods.find(
        (method) =>
          method.paymentMethodID === PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO,
      ),
    [paymentMethods],
  );
  const bsMethod = useMemo(
    () =>
      paymentMethods.find(
        (method) =>
          method.paymentMethodID === PaymentMethodEnum.VUELTO_EFECTIVO &&
          method.currencyID === 1,
      ),
    [paymentMethods],
  );
  const usdMethod = useMemo(
    () =>
      paymentMethods.find(
        (method) =>
          method.paymentMethodID === PaymentMethodEnum.VUELTO_EFECTIVO &&
          method.currencyID === 2,
      ),
    [paymentMethods],
  );

  const zelleMethod = useMemo(
    () =>
      paymentMethods.find(
        (method) =>
          method.paymentMethodID === PaymentMethodEnum.REINTEGRO_ZELLE,
      ),
    [paymentMethods],
  );
  const instantCreditMethod = useMemo(
    () =>
      paymentMethods.find(
        (method) =>
          method.paymentMethodID === PaymentMethodEnum.CREDITO_INMEDIATO,
      ),
    [paymentMethods],
  );

  const instantCreditCMMethod = useMemo(
    () =>
      paymentMethods.find(
        (e) => e.paymentMethodID === PaymentMethodEnum.CREDITO_INMEDIATO_CM,
      ),
    [paymentMethods],
  );

  const instantCreditAccountMethod = useMemo(
    () =>
      paymentMethods.find(
        (e) => e.paymentMethodID === PaymentMethodEnum.CREDITO_INMEDIATO_CUENTA,
      ),
    [paymentMethods],
  );

  const usdBalance = useMemo(
    () =>
      (dailyClosures.find((c) => c.currencyID === 2)?.balance ?? 0).toFixed(2),
    [dailyClosures],
  );

  const bsBalance = useMemo(
    () =>
      (dailyClosures.find((c) => c.currencyID === 1)?.balance ?? 0).toFixed(2),
    [dailyClosures],
  );

  const maxZelleChange = useMemo(() => {
    const zellePayments = payments.reduce((sum, payment) => {
      if (
        payment.status === PaymentStatusEnum.APPROVE &&
        payment.paymentMethod.paymentMethodID === PaymentMethodEnum.ZELLE
      ) {
        return sum + payment.amount;
      }
      return sum;
    }, 0);

    return Math.min(
      +currencyExchange(-remaining, exchanges, "USD").toFixed(2),
      +currencyExchange(zellePayments, exchanges, "USD").toFixed(2),
    );
  }, [payments, exchanges, remaining]);

  const isSuperadmin = useMemo(() => {
    return user.user?.roleName === "Superadministrador";
  }, [user.user?.roleName]);

  const tabs = useMemo(() => {
    let tempTabs = [
      {
        name: "Crédito Inmediato",
      },
      {
        name: "Efectivo",
        hidden: hasRetention && !isSuperadmin,
      },
      {
        name: "Pago Móvil",
        hidden: !isSuperadmin,
      },
    ];

    const filteredPayments = payments.filter(
      (p) =>
        p.paymentMethod.paymentMethodID !== PaymentMethodEnum.AJUSTE_DECIMAL &&
        p.paymentMethod.paymentMethodID !==
          PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL &&
        p.paymentMethod.paymentMethodID !==
          PaymentMethodEnum.RETENCION_DE_ISRL &&
        p.paymentMethod.paymentMethodID !==
          PaymentMethodEnum.RETENCION_DE_IVA &&
        p.paymentMethod.paymentMethodID !== PaymentMethodEnum.REINTEGRO &&
        p.paymentMethod.paymentMethodID !== PaymentMethodEnum.REINTEGRO_ZELLE &&
        p.paymentMethod.paymentMethodID !==
          PaymentMethodEnum.CREDITO_INMEDIATO &&
        p.paymentMethod.paymentMethodID !==
          PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO &&
        p.paymentMethod.paymentMethodID !== PaymentMethodEnum.VUELTO_EFECTIVO &&
        p.status === PaymentStatusEnum.APPROVE,
    );

    const lastPayment = filteredPayments.at(-1);
    if (
      lastPayment?.paymentMethod?.paymentMethodID === PaymentMethodEnum.ZELLE &&
      lastPayment?.status === PaymentStatusEnum.APPROVE
    ) {
      tempTabs.push({
        name: "Zelle",
      });
    }

    return tempTabs;
  }, [isSuperadmin, hasRetention, payments]);

  const [tab, setTab] = useState(tabs.findIndex((t) => t.name === initialTab));

  // Submit form
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (tab === tabs.findIndex((t) => t.name === "Pago Móvil")) {
        formik.setFieldValue("currency", "BS");
        if (superadminOption.id === 2) {
          if (!reintegroMethod) {
            alertService.error(
              "Método de Reintegro no habilitado. Contacte a soporte.",
            );
            return;
          }
          formik.setFieldValue("destBankID", undefined);
        } else if (!b2pMethod) {
          alertService.error(
            "Método de Pago Móvil B2P no habilitado. Contacte a soporte.",
          );
          return;
        }
      } else if (tab === tabs.findIndex((t) => t.name === "Efectivo")) {
        formik.setFieldValue("destBankID", undefined);
        if (formik.values.currency === "BS") {
          if (!bsMethod) {
            alertService.error(
              "Método de Vuelto en Bolívares en efectivo no habilitado. Contacte a soporte.",
            );
            return;
          }
        } else if (!usdMethod) {
          alertService.error(
            "Método de Vuelto en Dólares en efectivo no habilitado. Contacte a soporte.",
          );
          return;
        }
      } else if (
        tab === tabs.findIndex((t) => t.name === "Crédito Inmediato")
      ) {
        if (!instantCreditMethod) {
          alertService.error(
            "Método de Crédito Inmediato no habilitado. Contacte a soporte.",
          );
          return;
        }
      } else if (tab === tabs.findIndex((t) => t.name === "Zelle")) {
        if (!zelleMethod) {
          alertService.error(
            "Método de Zelle no habilitado. Contacte a soporte.",
          );
          return;
        }
        if (
          (formik.values.phone.trim() === "" ||
            formik.values.phone === undefined) &&
          (formik.values.email?.trim() === "" ||
            formik.values.email === undefined)
        ) {
          alertService.error(
            "Debe ingresar un correo o teléfono afiliado a la cuenta Zelle.",
          );
          return;
        }
        if (
          formik.values.phone.trim() !== "" &&
          formik.values.phone !== undefined &&
          formik.values.email?.trim() !== "" &&
          formik.values.email !== undefined
        ) {
          alertService.error(
            "Debe ingresar solo el correo o el teléfono afiliado a la cuenta Zelle. (No ambos)",
          );
          return;
        }
        formik.setFieldValue("currency", "USD");
        formik.setFieldValue("destBankID", undefined);
      }

      const cleanClientIdentifier = cleanID(formik.values.clientIdentifier);
      formik.setFieldValue(
        "clientIdentifier",
        `${idType}${cleanClientIdentifier}`,
      );

      const errors = await formik.validateForm();

      if (Object.keys(errors).length === 0) {
        setOpenModal(false);
        formik.handleSubmit(e);
      } else {
        for (const key in errors) {
          alertService.error(errors[key as keyof typeof errors] as string);
        }
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formik.values,
      setOpenModal,
      paymentMethods,
      tab,
      idType,
      superadminOption,
      reintegroMethod,
      b2pMethod,
      bsMethod,
      usdMethod,
      instantCreditMethod,
    ],
  );

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = formatGooglePhone(e.target.value);
    formik.setFieldValue("phone", value);
  };

  const handleZellePhoneNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = formatGooglePhoneUSA(e.target.value);
    formik.setFieldValue("phone", value);
  };

  const handlePmAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    max: number,
  ) => {
    const raw = e.target.value;
    const parsed = raw === "" ? 0 : Number.parseFloat(raw.replace(",", "."));
    let amount = Number.isFinite(parsed) ? parsed : 0;
    if (amount < 0) amount = 0;
    if (amount > max) amount = Math.max(max, 0);

    formik.setFieldValue("amount", +amount.toFixed(2));
  };

  useEffect(() => {
    const buCode = user.businessUnit?.code;
    if (!buCode) return;

    // Get daily amount change limit
    const getClosures = async () => {
      loaderService.start();
      const response = await getDailyClosures(buCode);
      if (!response.didError && response.model) {
        setDailyClosures(
          response.model.sort((a, b) => {
            return moment(a.closeDate).isBefore(moment(b.closeDate)) ? 1 : -1;
          }),
        );
      }
      loaderService.stop();
    };

    getClosures();
  }, [user.businessUnit?.code]);

  // Sum cash payments in Bs and USD and set max amount to pay
  useEffect(() => {
    // Sum cash payments
    let amountBs = 0;
    let amountUsd = 0;
    for (const payment of payments) {
      if (payment.paymentMethod.currencyID === 1) {
        amountBs += payment.amount;
      } else {
        amountUsd += payment.amount;
      }
    }

    setSumBs(amountBs);
    setSumUsd(amountUsd);

    // Set max amount to pay
    if (isSuperadmin || isRefund) {
      setMaxUsd(+currencyExchange(-remaining, exchanges, "USD").toFixed(2));
      setMaxBs(+currencyExchange(-remaining, exchanges, "BS").toFixed(2));
    } else {
      setMaxUsd(
        Math.min(
          +currencyExchange(sumUsd, exchanges, "USD").toFixed(2), // Sum cash payments in USD
          +currencyExchange(-remaining, exchanges, "USD").toFixed(2), // Amount to pay in USD
          +usdBalance, // Available USD balance
        ),
      );
      setMaxBs(
        Math.min(
          +currencyExchange(-remaining, exchanges, "BS").toFixed(2), // Amount to pay in Bs
          +bsBalance, // Available Bs balance
        ),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payments,
    exchanges,
    remaining,
    sumBs,
    sumUsd,
    usdBalance,
    bsBalance,
    isSuperadmin,
  ]);

  useEffect(() => {
    setIdType(owner?.abreviationName[0] ?? "V");
    if (tab === tabs.findIndex((t) => t.name === "Pago Móvil")) {
      if (availableChange <= 0)
        alertService.error(
          "No hay saldo disponible para vuelto por pago móvil",
        );
      if (superadminOption.id === 2) {
        formik.setFieldValue("paymentMethod", reintegroMethod);
      } else {
        formik.setFieldValue("paymentMethod", b2pMethod);
        formik.setFieldValue("destBankID", BankEnum.BANCARIBE);
      }
      formik.setFieldValue(
        "amount",
        Math.min(
          +currencyExchange(availableChange, exchanges, "BS", "USD").toFixed(2),
          -remaining.toFixed(2),
        ),
      );
      formik.setFieldValue("phone", "");
    } else if (tab === tabs.findIndex((t) => t.name === "Efectivo")) {
      formik.setFieldValue("destBankID", undefined);
      if (formik.values.currency === "BS") {
        formik.setFieldValue("amount", +maxBs.toFixed(2));
        formik.setFieldValue("paymentMethod", bsMethod);
      } else {
        formik.setFieldValue("amount", +maxUsd.toFixed(2));
        formik.setFieldValue("paymentMethod", usdMethod);
      }
    } else if (tab === tabs.findIndex((t) => t.name === "Zelle")) {
      formik.setFieldValue("paymentMethod", zelleMethod);
      formik.setFieldValue("amount", maxZelleChange);
      formik.setFieldValue("currency", "USD");
      formik.setFieldValue("destBankID", undefined);
      formik.setFieldValue("phone", "");
    } else if (tab === tabs.findIndex((t) => t.name === "Crédito Inmediato")) {
      if (availableChange <= 0)
        alertService.error(
          "No hay saldo disponible para vuelto por Crédito Inmediato",
        );
      if (isRefund && !isSuperadmin) {
        formik.setFieldValue("paymentMethod", instantCreditCMMethod);
        formik.setFieldValue("amount", -remaining.toFixed(2));
      } else {
        formik.setFieldValue("paymentMethod", instantCreditMethod);
        formik.setFieldValue(
          "amount",
          Math.min(
            +currencyExchange(availableChange, exchanges, "BS", "USD").toFixed(
              2,
            ),
            -remaining.toFixed(2),
          ),
        );
      }
      formik.setFieldValue("currency", "BS");
      formik.setFieldValue("destBankID", BankEnum.R4);
    } else if (tab === tabs.findIndex((t) => t.name === "Zelle")) {
      formik.setFieldValue("paymentMethod", zelleMethod);
      formik.setFieldValue("amount", maxZelleChange);
      formik.setFieldValue("currency", "USD");
      formik.setFieldValue("destBankID", undefined);
      formik.setFieldValue("phone", "");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    formik.values.currency,
    maxBs,
    maxUsd,
    availableChange,
    remaining,
    superadminOption,
    reintegroMethod,
    b2pMethod,
    bsMethod,
    usdMethod,
    instantCreditMethod,
    zelleMethod,
  ]);

  return (
    <Modal
      openModal={openModal}
      setOpenModal={() => {}}
      className="w-auto max-w-md"
    >
      <div className="bg-white pl-4 pb-4">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row mb-4">
            <h2 className="text-md font-semibold items-center">Cambio:</h2>
            <div className="flex flex-row justify-start pl-1">
              <p className="text-sm font-semibold text-gray-600 truncate place-self-center">
                {currencyExchangeText(-remaining, exchanges, "BS")}
              </p>
              <p className="text-xs text-gray-400 place-self-center ml-1">
                {currencyExchangeText(-remaining, exchanges, "USD")}
              </p>
            </div>
          </div>
          <div className="ml-3 flex h-7 items-center">
            <button
              type="button"
              className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onClick={() => setOpenModal(false)}
            >
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Close panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="mb-2">
          <MenuTab current={tab} onTabClick={setTab} tabs={tabs} />
        </div>
        {/* PM and IC change */}
        {(tab === tabs.findIndex((t) => t.name === "Pago Móvil") ||
          tab === tabs.findIndex((t) => t.name === "Crédito Inmediato")) && (
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="flex flex-row mb-4 w-full">
              <div className="flex flex-col">
                <FormSelect
                  containerClassName="rounded rounded-r-none"
                  name="identificationType"
                  type="text"
                  label="Tipo"
                  disabled={!isSuperadmin}
                  options={identificationTypes}
                  optionString={(identificationTypes: string) =>
                    identificationTypes
                  }
                  selected={idType}
                  onSelectOption={setIdType}
                />
              </div>
              <div className="flex flex-col w-full">
                <FormTextInput
                  className="rounded rounded-l-none"
                  name="clientIdentifier"
                  type="text"
                  label="Cédula del cliente"
                  placeholder="12345678"
                  disabled={!isSuperadmin}
                  onChange={formik.handleChange}
                  value={cleanID(formik.values.clientIdentifier)}
                  error={
                    formik.touched.clientIdentifier &&
                    formik.errors.clientIdentifier
                      ? formik.errors.clientIdentifier
                      : ""
                  }
                />
              </div>
            </div>
            <div className="mb-4">
              <FormTextInput
                name="phone"
                type="text"
                label="Teléfono"
                placeholder="04141234567"
                onChange={handlePhoneNumberChange}
                value={formik.values.phone}
                error={
                  formik.touched.phone && formik.errors.phone
                    ? formik.errors.phone
                    : ""
                }
              />
            </div>
            <div className="mb-4">
              <FormTextInput
                name="amount"
                type="number"
                step="any"
                disabled={!isSuperadmin}
                label="Monto a pagar en Bolívares"
                onChange={(e) => handlePmAmountChange(e, maxBs)}
                value={formik.values.amount}
                error={
                  formik.touched.amount && formik.errors.amount
                    ? formik.errors.amount
                    : ""
                }
              />
            </div>
            <div className="flex flex-col mb-4">
              {false && ( // Destination bank select hidden for now
                <FormSelect
                  label="Banco remitente"
                  name="destBankID"
                  options={
                    paymentMethods.find(
                      (method) =>
                        method.paymentMethodID ===
                        PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO,
                    )?.bankAccounts ?? []
                  }
                  optionString={(bank) => {
                    return bank
                      ? `${formatName(
                          bank.bankName!,
                        )} (${bank.accountNumber?.slice(-4)})`
                      : "Buscar...";
                  }}
                  selected={
                    paymentMethods
                      .find(
                        (method) =>
                          method.paymentMethodID ===
                          PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO,
                      )
                      ?.bankAccounts?.find(
                        (b) => b.bankID === formik.values.destBankID,
                      ) ?? null
                  }
                  onSelectOption={(bank) => {
                    formik.setFieldValue("destBankID", bank?.bankID);
                  }}
                  error={
                    formik.touched.destBankID && formik.errors.destBankID
                      ? formik.errors.destBankID
                      : ""
                  }
                />
              )}

              <FormSelect
                label="Banco destino"
                name="bank"
                options={bankList! || []}
                optionString={(bank) =>
                  `${
                    bank.descripcion
                      ? `${formatName(bank.descripcion).toUpperCase()} (${
                          bank.codigo
                        })`
                      : "Buscar..."
                  }`
                }
                selected={formik.values.bank}
                onSelectOption={(bank) => formik.setFieldValue("bank", bank)}
                error={
                  formik.touched.bank && formik.errors.bank
                    ? formik.errors.bank
                    : ""
                }
              />
            </div>
            {superadminOption.id === 2 && (
              <div className="mb-4">
                <FormTextInput
                  name="reference"
                  type="text"
                  label="Referencia"
                  onChange={(e) =>
                    formik.setFieldValue("reference", e.target.value)
                  }
                  value={formik.values.reference}
                  error={
                    formik.touched.reference && formik.errors.reference
                      ? formik.errors.reference
                      : ""
                  }
                />
              </div>
            )}
            {tab === tabs.findIndex((t) => t.name === "Crédito Inmediato") &&
              false && ( // TODO: Phase 2
                <LinkText
                  className="mb-4"
                  onClick={() => {
                    alertService.info("Funcionalidad en desarrollo");
                  }}
                  text="Cambiar a Crédito Inmediato cuenta bancaria"
                />
              )}
            {tab === tabs.findIndex((t) => t.name === "Crédito Inmediato") &&
              isRefund &&
              !isSuperadmin && (
                <p className="text-sm text-gray-500 text-left mb-4">
                  Se procesará una solicitud de reintegro por Crédito Inmediato
                  a Casa Matriz.
                </p>
              )}
            {isSuperadmin &&
              tab === tabs.findIndex((t) => t.name === "Pago Móvil") && (
                <div className="flex flex-col mb-4">
                  <div className="flex flex-col pr-2 w-full">
                    <FormSelect
                      label="Procesar vuelto / Registrar vuelto"
                      name="type"
                      options={superadminOptions ?? []}
                      optionString={(type) => `${type.name ?? "Buscar..."}`}
                      selected={superadminOption}
                      onSelectOption={(option) => setSuperadminOption(option)}
                    />
                  </div>
                  <p className="text-sm text-gray-500 text-left">
                    - Al procesar el vuelto se usará el servicio de vuelto del
                    banco seleccionado como remitente.
                  </p>
                  <p className="text-sm text-gray-500 text-left">
                    - Al registrar el vuelto se guardará directamente como pago
                    procesado.
                  </p>
                </div>
              )}
            <PrimaryButton
              className="items-center justify-center w-full"
              type="submit"
            >
              {tab === tabs.findIndex((t) => t.name === "Crédito Inmediato") &&
              isRefund &&
              !isSuperadmin
                ? "Solicitar reintegro"
                : "Procesar cambio"}
            </PrimaryButton>
          </form>
        )}
        {/* Cash change */}
        {tab === tabs.findIndex((t) => t.name === "Efectivo") && (
          <form onSubmit={handleSubmit} className="flex flex-col">
            {(sumUsd > 0 || isSuperadmin) && (
              <div className="my-4">
                <label className="pr-4">
                  <input
                    className="mr-1"
                    type="radio"
                    value="0"
                    checked={radio === 0}
                    onChange={() => setRadio(0)}
                  />{" "}
                  Bolívares
                </label>
                <label>
                  <input
                    className="mr-1"
                    type="radio"
                    value="1"
                    checked={radio === 1}
                    onChange={() => setRadio(1)}
                  />{" "}
                  Dólares
                </label>
              </div>
            )}
            {radio === 0 &&
              (currencyExchange(-remaining, exchanges, "BS") <= +bsBalance ||
              isSuperadmin ? (
                <CashChange currencyID={1} formik={formik} max={maxBs} />
              ) : (
                <div className="bg-gray-200 rounded p-4 mb-4">
                  <p className="text-black ">
                    No hay suficiente balance en bolívares en efectivo para
                    poder procesar el cambio completo. Considere otra opción de
                    vuelto.
                  </p>
                </div>
              ))}
            {radio === 1 &&
              (currencyExchange(-remaining, exchanges, "USD") <= +usdBalance ||
              isSuperadmin ? (
                <CashChange currencyID={2} formik={formik} max={maxUsd} />
              ) : (
                <div className="bg-gray-200 rounded p-4 mb-4">
                  <p className="text-black ">
                    No hay suficiente balance en dolares en efectivo para poder
                    procesar el cambio completo. Considere otra opción de
                    vuelto.
                  </p>
                </div>
              ))}

            <PrimaryButton
              className="items-center justify-center w-full"
              type="submit"
              disabled={
                (radio === 0 &&
                  currencyExchange(-remaining, exchanges, "BS") > +bsBalance) ||
                (radio === 1 &&
                  currencyExchange(-remaining, exchanges, "USD") >
                    +usdBalance) ||
                formik.values.amount <= 0 ||
                isNaN(formik.values.amount)
              }
            >
              Procesar cambio
            </PrimaryButton>
          </form>
        )}
        {/* Zelle change */}
        {tab === tabs.findIndex((t) => t.name === "Zelle") && (
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="flex w-full justify-center">
              <div className="max-w-lg text-center rounded-lg shadow-lg w-fit p-6 border">
                <p className="text-sm text-gray-500 mb-1">
                  Para procesar una solicitud de reintegro{" "}
                  <span className="text-gray-900 font-bold">Zelle</span>, el
                  cliente debe indicar{" "}
                  <span className="text-gray-900 font-bold">
                    {" "}
                    el mismo correo electrónico o número de teléfono
                  </span>{" "}
                  que utilizó para realizar el pago original por Zelle.
                </p>
              </div>
            </div>
            <div className="my-4">
              <FormTextInput
                name="amount"
                type="number"
                step="any"
                label="Monto a pagar en Reintegro Zelle"
                disabled={true}
                onChange={async (e) =>
                  await handleAmountChange(e, formik, maxZelleChange)
                }
                value={formik.values.amount}
                error={
                  formik.touched.amount && formik.errors.amount
                    ? formik.errors.amount
                    : ""
                }
              />
            </div>
            <div className="mb-4">
              <FormTextInput
                name="email"
                type="email"
                label="Correo"
                placeholder="Correo afiliado a la cuenta Zelle"
                onChange={(e) => formik.setFieldValue("email", e.target.value)}
                value={formik.values.email ?? ""}
                error={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : ""
                }
              />
            </div>
            <div className="mb-4">
              <FormTextInput
                name="phone"
                type="text"
                label="Teléfono"
                placeholder="Teléfono afiliado a la cuenta Zelle"
                onChange={handleZellePhoneNumberChange}
                value={formik.values.phone}
                error={
                  formik.touched.phone && formik.errors.phone
                    ? formik.errors.phone
                    : ""
                }
              />
            </div>
            <PrimaryButton
              className="items-center justify-center w-full"
              type="submit"
            >
              Solicitar reintegro
            </PrimaryButton>
          </form>
        )}
        {isSuperadmin && tab === 0 && (
          <SecondaryButton
            className="items-center justify-center w-full mt-2"
            onClick={async () => {
              formik.setFieldValue("paymentMethod", instantCreditCMMethod);
              const errors = await formik.validateForm();
              if (Object.keys(errors).length === 0) {
                setOpenModal(false);
                formik.handleSubmit();
              } else {
                for (const key in errors) {
                  alertService.error(
                    errors[key as keyof typeof errors] as string,
                  );
                }
              }
            }}
          >
            Solicitar Reintegro
          </SecondaryButton>
        )}
      </div>
    </Modal>
  );
};
export default ChangeModal;
